import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import PropTypes from "prop-types";
import LoadingSpinner from "components/Common/LoadingSpinner";
import { getFirebaseAuth } from "api/firebase/firebase";
import useUserStore from "stores/UserStore";
import { validateEmailDomain } from "utils/validationUtils";
import { ROUTES } from "./AppConfig";

/**
 * Does the customization's email-domain restriction (the one Login enforces)
 * let this user in? True when no restriction is set.
 */
const isEmailDomainAllowed = (config, email) => {
  const restriction = config?.emailDomainRestriction;
  const allowedDomains = restriction?.allowedDomains || [];
  if (!restriction?.enabled || allowedDomains.length === 0) return true;
  return validateEmailDomain(email, allowedDomains).isValid;
};

/**
 * The multicriteria (suitability) route. Only for signed-in, non-anonymous
 * users of a customization with the `suitabilityMapping` flag.
 *
 * On a reload or a bookmark, Firebase restores the session asynchronously:
 * `currentUser` is null until then, and the config may still be loading. The
 * decision waits for both, or the deep link would redirect home at once. The
 * store's `loading` flag can't be used here: only the Login screen's auth
 * listener resolves it, and Login is not mounted on this route.
 *
 * For the same reason the user store is filled here: the page reads the
 * signed-in user from it (save, "my maps"), and only Login's listener would
 * otherwise call `authenticate`, so a restored session looked like a guest.
 * The same email-domain rule as Login applies, so this route never lets in a
 * user Login would sign out.
 */
function SuitabilityRoute({ config, component: Component }) {
  const [authRestored, setAuthRestored] = useState(false);

  useEffect(() => {
    let active = true;
    const markRestored = () => {
      if (active) setAuthRestored(true);
    };
    getFirebaseAuth().authStateReady().then(markRestored, markRestored);
    return () => {
      active = false;
    };
  }, []);

  const ready = authRestored && config !== null;
  const firebaseUser = ready ? getFirebaseAuth().currentUser : null;
  const allowed = Boolean(
    config?.featureFlags?.suitabilityMapping &&
    firebaseUser &&
    firebaseUser.isAnonymous === false &&
    isEmailDomainAllowed(config, firebaseUser.email),
  );

  useEffect(() => {
    if (allowed && !useUserStore.getState().authenticated) {
      useUserStore.getState().authenticate(firebaseUser);
    }
  }, [allowed, firebaseUser]);

  if (!ready) {
    return <LoadingSpinner />;
  }
  return allowed ? <Component /> : <Redirect to={ROUTES.ROOT} />;
}

SuitabilityRoute.propTypes = {
  config: PropTypes.object,
  component: PropTypes.elementType.isRequired,
};

export default SuitabilityRoute;
